import React from 'react';
import { Card, CardContent } from './ui/card';

// Shared metrics section used by the "Simulation & Drill Metrics" and
// "Incident Metrics" dashboard widgets: gradient icon header plus the
// full-width responsive card grid.
const DashboardMetricsSection = ({ title, subtitle, icon: Icon, iconGradient, metrics = [] }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className={`bg-gradient-to-r ${iconGradient} p-3 rounded-xl`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600">{subtitle}</p>
          </div>
        </div>
      </div>

      {/* Enhanced Metrics Cards - full-width responsive grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
        {metrics.map((metric, index) => {
          const content = (
            <CardContent className="px-5 py-5 h-36 flex flex-col justify-between relative">
              {/* Top section - Title and Value with balanced spacing */}
              <div className="flex flex-col">
                {/* Title - optimized for single row */}
                <div className="h-5 mb-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide leading-tight">
                    {metric.title}
                  </p>
                </div>

                {/* Value section - balanced size */}
                <div className="h-12 flex items-center">
                  {metric.color && (
                    <div className={`w-2.5 h-2.5 rounded-full ${metric.color} mr-2 shadow-sm flex-shrink-0`}></div>
                  )}
                  <p className="text-3xl font-bold text-gray-900 leading-none">{metric.value}</p>
                </div>
              </div>

              {/* Bottom section - Progress bar only */}
              <div className="h-9 flex items-center mt-2">
                {/* Progress bar for colored metrics */}
                <div className="w-full">
                  {metric.color ? (
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${metric.color} transition-all duration-1000 shadow-sm`}
                        style={{ width: `${Math.min(parseInt(metric.value || 0) * 8 + 8, 100)}%` }}
                      ></div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              </div>
            </CardContent>
          );

          return (
            <Card
              key={metric.id || metric.title || index}
              className={`group hover:shadow-xl transition-all duration-300 bg-white border border-gray-100 shadow-md overflow-hidden rounded-lg w-full ${
                metric.onClick ? 'hover:-translate-y-0.5 hover:border-blue-200' : ''
              }`}
            >
              {metric.onClick ? (
                <button
                  type="button"
                  onClick={metric.onClick}
                  aria-label={metric.ariaLabel || `Open ${metric.title}`}
                  data-testid={`dashboard-metric-${metric.id || index}`}
                  className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
                >
                  {content}
                </button>
              ) : content}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardMetricsSection;
